import React from 'react';
import { useTranslation } from 'react-i18next';
import ProductAutocomplete from './ProductAutocomplete';
import { normalizeNumericInput, toNumber } from '../utils/numericInput';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

/**
 * PurchaseOrderItemRow
 * One line of the purchase order grid. The line total is display-only and
 * always quantity * unit_price - the server recomputes the same product on
 * save, so this is a preview, never the value that gets persisted.
 */
const PurchaseOrderItemRow = ({
  item,
  index,
  products,
  measures = [],
  onItemChange,
  onItemRemove
}) => {
  const { t } = useTranslation();

  const lineTotal = toNumber(item.quantity) * toNumber(item.unit_price);

  // Stores the raw (normalised) string rather than a parsed number so the field
  // can be cleared and never accumulates a stale leading zero.
  const handleNumericChange = (field) => (e) => {
    onItemChange(index, field, normalizeNumericInput(e.target.value));
  };

  const handleProductSelect = (product) => {
    onItemChange(index, 'productId', product.id);
    onItemChange(index, 'productName', product.name);
    onItemChange(index, 'description', product.name);
    onItemChange(
      index,
      'unit_price',
      normalizeNumericInput(product.cost ?? product.price ?? 0)
    );
  };

  return (
    <tr>
      <td className="text-center">{index + 1}</td>

      <td>
        <ProductAutocomplete
          products={products}
          selectedProduct={item.productId ? { id: item.productId, name: item.productName } : null}
          onSelect={handleProductSelect}
        />
      </td>

      <td>
        <input
          type="text"
          value={item.description || ''}
          onChange={(e) => onItemChange(index, 'description', e.target.value)}
          className="form-input form-input-sm"
          aria-label={t('table.description')}
        />
      </td>

      <td>
        <select
          value={item.measure ?? ''}
          onChange={(e) => onItemChange(index, 'measure', e.target.value || null)}
          className="form-select form-select-sm"
          aria-label={t('table.measure')}
        >
          <option value="">—</option>
          {measures.map((measure) => (
            <option key={measure.id} value={measure.id}>
              {measure.name}
            </option>
          ))}
        </select>
      </td>

      <td className="text-center">
        <input
          type="text"
          inputMode="decimal"
          value={item.quantity ?? ''}
          onChange={handleNumericChange('quantity')}
          className="form-input form-input-sm"
          aria-label={t('table.quantity')}
        />
      </td>

      <td className="text-center">
        <input
          type="text"
          inputMode="decimal"
          value={item.unit_price ?? ''}
          onChange={handleNumericChange('unit_price')}
          className="form-input form-input-sm"
          aria-label={t('table.unitPrice')}
        />
      </td>

      <td className="text-center">
        <input
          type="checkbox"
          checked={!!item.is_inventoried}
          onChange={(e) => onItemChange(index, 'is_inventoried', e.target.checked)}
          aria-label={t('purchases.isInventoried')}
        />
      </td>

      <td className="text-right text-bold">{formatCurrency(lineTotal)}</td>

      <td className="text-center">
        <button
          type="button"
          onClick={() => onItemRemove(index)}
          className="btn btn-danger btn-small"
        >
          {t('common.delete')}
        </button>
      </td>
    </tr>
  );
};

export default PurchaseOrderItemRow;
